import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";

class PreviewErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: { children: React.ReactNode }): void {
    if (prevProps.children !== this.props.children && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.35)",
          }}
        >
          <span style={{ color: "#fca5a5", fontSize: "0.72em", fontFamily: "Inter, sans-serif" }}>
            Preview component error
          </span>
        </div>
      );
    }

    return this.props.children;
  }
}

// ─── Remotion API stubs for client-side preview ──────────────────

interface SpringParams {
  frame: number;
  fps: number;
  config?: { damping?: number; stiffness?: number; mass?: number };
  durationInFrames?: number;
}

function previewSpring(params: SpringParams): number {
  const { frame, fps, config = {} } = params;
  const { damping = 10, stiffness = 100, mass = 1 } = config;
  if (frame <= 0) return 0;

  const t = frame / fps;
  const omega = Math.sqrt(stiffness / mass);
  const zeta = damping / (2 * Math.sqrt(stiffness * mass));

  if (zeta >= 1) {
    return Math.min(1, 1 - Math.exp(-omega * t) * (1 + omega * t));
  }

  const omegaD = omega * Math.sqrt(1 - zeta * zeta);
  const result =
    1 -
    Math.exp(-zeta * omega * t) *
      (Math.cos(omegaD * t) + ((zeta * omega) / omegaD) * Math.sin(omegaD * t));
  return Math.max(0, Math.min(2, result));
}

function previewInterpolate(
  value: number,
  inputRange: number[],
  outputRange: number[],
  options?: { extrapolateLeft?: string; extrapolateRight?: string }
): number {
  if (inputRange.length < 2 || outputRange.length < 2) return outputRange[0] ?? 0;

  let clampedValue = value;
  if (options?.extrapolateLeft === "clamp") {
    clampedValue = Math.max(clampedValue, inputRange[0]);
  }
  if (options?.extrapolateRight === "clamp") {
    clampedValue = Math.min(clampedValue, inputRange[inputRange.length - 1]);
  }

  let i = 0;
  while (i < inputRange.length - 2 && clampedValue > inputRange[i + 1]) i++;

  const inMin = inputRange[i];
  const inMax = inputRange[i + 1];
  const outMin = outputRange[i];
  const outMax = outputRange[i + 1];

  if (inMax === inMin) return outMin;
  const t = (clampedValue - inMin) / (inMax - inMin);
  return outMin + t * (outMax - outMin);
}

const AbsoluteFillStub: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({
  children,
  style,
}) => (
  <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, ...style }}>
    {children}
  </div>
);

const SequenceStub: React.FC<{
  children?: React.ReactNode;
  from?: number;
  durationInFrames?: number;
}> = ({ children }) => <>{children}</>;

const ImgStub: React.FC<{ src?: string; style?: React.CSSProperties }> = ({ src, style }) => (
  <img src={src} style={style} alt="" />
);

// ─── @remotion/shapes stubs ──────────────────────────────────────

interface ShapeStubProps {
  width?: number;
  height?: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  cornerRadius?: number;
  style?: React.CSSProperties;
  radius?: number;
  points?: number;
  innerRadius?: number;
  edgeRoundness?: number;
  direction?: string;
  length?: number;
}

const RectStub: React.FC<ShapeStubProps> = ({
  width = 100,
  height = 100,
  fill = "#fff",
  cornerRadius = 0,
  style,
}) => (
  <div
    style={{
      width,
      height,
      backgroundColor: fill,
      borderRadius: cornerRadius,
      ...style,
    }}
  />
);

const CircleStub: React.FC<ShapeStubProps> = ({ radius = 50, fill = "#fff", style }) => (
  <div
    style={{
      width: (radius ?? 50) * 2,
      height: (radius ?? 50) * 2,
      borderRadius: "50%",
      backgroundColor: fill,
      ...style,
    }}
  />
);

const TriangleStub: React.FC<ShapeStubProps> = ({
  length = 100,
  fill = "#fff",
  direction = "up",
  style,
}) => {
  const size = length ?? 100;
  const borderMap: Record<string, React.CSSProperties> = {
    up: {
      borderLeft: `${size / 2}px solid transparent`,
      borderRight: `${size / 2}px solid transparent`,
      borderBottom: `${size}px solid ${fill}`,
    },
    down: {
      borderLeft: `${size / 2}px solid transparent`,
      borderRight: `${size / 2}px solid transparent`,
      borderTop: `${size}px solid ${fill}`,
    },
    left: {
      borderTop: `${size / 2}px solid transparent`,
      borderBottom: `${size / 2}px solid transparent`,
      borderRight: `${size}px solid ${fill}`,
    },
    right: {
      borderTop: `${size / 2}px solid transparent`,
      borderBottom: `${size / 2}px solid transparent`,
      borderLeft: `${size}px solid ${fill}`,
    },
  };
  return <div style={{ width: 0, height: 0, ...borderMap[direction ?? "up"], ...style }} />;
};

const StarStub: React.FC<ShapeStubProps> = ({ style }) => (
  <div style={{ fontSize: 48, ...style }}>★</div>
);

const EllipseStub: React.FC<ShapeStubProps> = ({ style }) => (
  <div style={{ width: 100, height: 60, borderRadius: "50%", backgroundColor: "#fff", ...style }} />
);

const PieStub: React.FC<ShapeStubProps> = ({ style }) => (
  <div
    style={{ width: 100, height: 100, borderRadius: "50%", backgroundColor: "#fff", ...style }}
  />
);

// ─── @remotion/transitions stubs ─────────────────────────────────

const TransitionSeriesStub: React.FC<{ children?: React.ReactNode }> & {
  Sequence: React.FC<{ children?: React.ReactNode; durationInFrames?: number }>;
  Transition: React.FC<{ children?: React.ReactNode; presentation?: unknown; timing?: unknown }>;
} = ({ children }) => <>{children}</>;

TransitionSeriesStub.Sequence = ({ children }) => <>{children}</>;
TransitionSeriesStub.Transition = () => null;

const linearTimingStub = (_opts?: { durationInFrames?: number }) => ({});
const springTimingStub = (_opts?: {
  config?: Record<string, number>;
  durationInFrames?: number;
}) => ({});
const fadeStub = () => ({});
const slideStub = (_opts?: { direction?: string }) => ({});
const wipeStub = (_opts?: { direction?: string }) => ({});

// ─── Dynamic component renderer ─────────────────────────────────

interface DynamicOverlayPreviewProps {
  compiled: string;
  currentFrame: number;
  fps: number;
  width: number;
  height: number;
  durationInFrames: number;
}

export function DynamicOverlayPreview({
  compiled,
  currentFrame,
  fps,
  width,
  height,
  durationInFrames,
}: DynamicOverlayPreviewProps) {
  const frameRef = useRef(currentFrame);
  frameRef.current = currentFrame;

  const configRef = useRef({ fps, width, height, durationInFrames, id: "preview" });
  configRef.current = { fps, width, height, durationInFrames, id: "preview" };

  const fakeUseCurrentFrame = useCallback(() => frameRef.current, []);
  const fakeUseVideoConfig = useCallback(() => configRef.current, []);

  const Component = useMemo(() => {
    if (!compiled) return null;
    try {
      const createComponent = new Function(
        "React",
        "useCurrentFrame",
        "useVideoConfig",
        "spring",
        "interpolate",
        "AbsoluteFill",
        "Sequence",
        "Img",
        "useState",
        "useMemo",
        "useRef",
        "useEffect",
        // @remotion/shapes
        "Rect",
        "Circle",
        "Triangle",
        "Star",
        "Ellipse",
        "Pie",
        // @remotion/transitions
        "TransitionSeries",
        "linearTiming",
        "springTiming",
        "fade",
        "slide",
        "wipe",
        compiled
      );

      return createComponent(
        React,
        fakeUseCurrentFrame,
        fakeUseVideoConfig,
        previewSpring,
        previewInterpolate,
        AbsoluteFillStub,
        SequenceStub,
        ImgStub,
        useState,
        useMemo,
        useRef,
        useEffect,
        // @remotion/shapes stubs
        RectStub,
        CircleStub,
        TriangleStub,
        StarStub,
        EllipseStub,
        PieStub,
        // @remotion/transitions stubs
        TransitionSeriesStub,
        linearTimingStub,
        springTimingStub,
        fadeStub,
        slideStub,
        wipeStub
      ) as React.FC;
    } catch (err) {
      console.error("[DynamicOverlayPreview] Failed to create component:", err);
      return null;
    }
  }, [compiled, fakeUseCurrentFrame, fakeUseVideoConfig]);

  if (!Component) {
    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0,0,0,0.6)",
        }}
      >
        <span style={{ color: "#ef4444", fontSize: "0.7em", fontFamily: "Inter, sans-serif" }}>
          ⚠️ Preview error
        </span>
      </div>
    );
  }

  return (
    <div key={currentFrame} style={{ position: "absolute", inset: 0 }}>
      <PreviewErrorBoundary>
        <Component />
      </PreviewErrorBoundary>
    </div>
  );
}
